/**
 * Fetches the rows beyond the server-rendered first page, holds them as pending fragments until
 * the visitor engages, and keeps the viewport steady while the table is rebuilt.
 *
 * Calls on sibling mixins via `this`: Sorting (refreshTable, pendingFragments, hasPendingFragments,
 * currentSortParam), ColumnVisibility (stripInlineColumnDisplay), UrlAndScoring
 * (getPprSuperflexSlug, appendPersistentParams, selectedWeek), Filtering (selectedFantasyPosition,
 * rookiesOnly), CoreState (basePath, researchDepth).
 */

export default () => ({
    loading: false,
    abortController: null,
    // Track user interactions to enable auto-insert
    userHasInteracted: false,
    // Scroll detection setup with direction tracking
    scrollDirection: 'down',
    scrollThreshold: 200, // Distance from bottom to trigger load
    isNearEnd: false,
    userHasScrolled: false,
    _preservingScroll: false,
    lastScrollY: 0,
    cleanupScrollListener: null,

    initRowLoading(initialPprSuperflexSlug) {
        this.setupScrollDetection();
        this.setupInteractionTracking();

        // Set initial scroll position
        const scrollContainer = this.getScrollContainer();
        this.lastScrollY = scrollContainer === window ? window.scrollY : scrollContainer.scrollTop;

        // Load additional rows for the rankings table, using the slug the initial
        // rows were rendered with so both sets share one ordering
        this.loadRows(25, 225, 0, true, initialPprSuperflexSlug);
    },

    async loadRows(offset = 25, limit = 225, delay = 0, fetchNextBatch = false, pprSuperflexSlug = null) {
        if (this.loading) {
            console.warn('Load rows already in progress, skipping new request');
            return;
        }
        if (this.abortController) {
            this.abortController.abort('abort-ongoing'); // Abort any ongoing fetch request
        }
        this.abortController = new AbortController();
        if (delay > 0) {
            await new Promise(resolve => setTimeout(resolve, delay));
        }
        this.loading = true;
        let url = `/${this.basePath}/load-rows?offset=${offset}`;
        if (limit && limit > 0) {
            url += `&limit=${limit}`;
        }
        url += `&fantasyPosition=${this.selectedFantasyPosition}&pprSuperflexSlug=${pprSuperflexSlug ?? this.getPprSuperflexSlug()}`;
        if (this.basePath === 'dynasty-rankings') {
            url += `&playerGroup=${this.rookiesOnly ? 'rookies' : 'all'}`;
        }
        url += `&sort=${this.currentSortParam}`;
        if ('weekly-rankings' === this.basePath && this.selectedWeek) {
            url += `&week=${this.selectedWeek}`;
        }
        if (['rankings', 'weekly-rankings', 'ros-rankings'].includes(this.basePath)) {
            // include research depth for rankings
            url += `&researchDepth=${this.researchDepth}`;
        }
        // Keep iframe pagination/load-more requests in the same partner context.
        url = this.appendPersistentParams(url);
        try {
            const response = await fetch(url, {
                signal: this.abortController.signal,
            });
            if (!response.ok) {
                throw new Error(`Failed to load rows: ${response.status} ${response.statusText}`);
            }
            const html = await response.text();
            const tempTable = document.createElement('table');
            tempTable.innerHTML = html;
            // Fetched rows carry SSR inline display:none on depth-hidden columns; clear it
            // while still off-DOM - the generated column stylesheet owns visibility
            this.stripInlineColumnDisplay(tempTable);
            const frag = document.createDocumentFragment();
            while (tempTable.firstChild) {
                frag.appendChild(tempTable.firstChild);
            }

            // Store the fragment for later processing
            this.pendingFragments.push({
                fragment: frag,
                offset: offset,
                limit: limit,
            });

            // If user is near end or should auto-insert, trigger a table refresh
            if (this.isNearEnd || this.shouldAutoInsert()) {
                this.preserveTopScroll(() => this.refreshTable());
            }
        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error('Failed to load rows', err);
            }
        } finally {
            this.loading = false;
            if (this.abortController && !this.abortController.signal.aborted) {
                this.abortController = null; // Reset abort controller after use
            }
            if (fetchNextBatch) {
                // Fetch all remaining rows
                this.loadRows(offset + limit, 0, 0, false, pprSuperflexSlug);
            }
        }
    },
    cancelLoadRows() {
        this.pendingFragments = []; // Clear pending fragments on cancel
        if (this.abortController) {
            this.abortController.abort('force-cancel');
            this.abortController = null;
            this.loading = false;
        }
    },

    // Method to determine if we should automatically insert pending fragments
    shouldAutoInsert() {
        if (!this.hasPendingFragments()) {
            return false; // No pending fragments to insert
        }
        // Auto-insert if user has shown engagement (scrolled or clicked anything)
        if (this.userHasScrolled || this.userHasInteracted) {
            return true;
        }
        // Don't auto-insert if user hasn't engaged yet
        // (keeps the page lightweight for users who might not scroll)
        return false;
    },

    setupInteractionTracking() {
        const interactionEvents = ['click', 'keydown', 'touchstart', 'mousedown'];

        const markInteraction = () => {
            if (!this.userHasInteracted) {
                this.userHasInteracted = true;
            }
        };

        interactionEvents.forEach(eventType => {
            document.addEventListener(eventType, markInteraction, { once: true, passive: true });
        });
    },

    setupScrollDetection() {
        const scrollContainer = this.getScrollContainer();

        const checkScroll = () => {
            if (this._preservingScroll) return; // Skip if currently preserving scroll
            const currentScrollY = scrollContainer === window ? window.scrollY : scrollContainer.scrollTop;
            const delta = currentScrollY - this.lastScrollY;

            // Track scroll direction
            if (delta > 0) {
                this.scrollDirection = 'down';
            } else if (delta < 0) {
                this.scrollDirection = 'up';
            }

            // Mark that user has scrolled
            if (!this.userHasScrolled && Math.abs(delta) > 50) {
                this.userHasScrolled = true;
            }

            this.lastScrollY = currentScrollY;

            // Check if near end (only when scrolling down to avoid premature triggers)
            if (this.scrollDirection === 'down') {
                const tableRect = this.$refs.rankingsTable.getBoundingClientRect();
                const viewportHeight = scrollContainer === window ? window.innerHeight : scrollContainer.clientHeight;
                const isNearBottom = tableRect.bottom <= viewportHeight + this.scrollThreshold;

                if (isNearBottom && !this.isNearEnd) {
                    this.isNearEnd = true;
                    // Only trigger refresh if we have pending fragments
                    if (this.hasPendingFragments()) {
                        this.preserveTopScroll(() => this.refreshTable());
                    }
                }

                // Reset isNearEnd when user scrolls back up significantly
                if (!isNearBottom && this.isNearEnd && currentScrollY < this.lastScrollY - 100) {
                    this.isNearEnd = false;
                }
            }
        };

        // Throttle scroll events for better performance
        const throttledCheckScroll = this.throttle(checkScroll, 100);

        scrollContainer.addEventListener('scroll', throttledCheckScroll);
        window.addEventListener('resize', throttledCheckScroll);

        // Store cleanup function
        this.cleanupScrollListener = () => {
            scrollContainer.removeEventListener('scroll', throttledCheckScroll);
            window.removeEventListener('resize', throttledCheckScroll);
        };
    },
    getScrollContainer() {
        // Look for a parent with specific scroll attributes or classes
        const table = this.$refs.rankingsTable;
        const scrollParent =
            table.closest('[data-scroll-container]') ||
            table.closest('.overflow-auto') ||
            table.closest('.overflow-y-auto') ||
            table.closest('.overflow-scroll');

        return scrollParent || window;
    },
    // Throttle utility for performance
    throttle(func, wait) {
        let timeout;
        let previous = 0;

        return function executedFunction(...args) {
            const now = Date.now();
            const remaining = wait - (now - previous);

            if (remaining <= 0 || remaining > wait) {
                if (timeout) {
                    clearTimeout(timeout);
                    timeout = null;
                }
                previous = now;
                func.apply(this, args);
            } else if (!timeout) {
                timeout = setTimeout(() => {
                    previous = Date.now();
                    timeout = null;
                    func.apply(this, args);
                }, remaining);
            }
        };
    },

    // Keep viewport steady(ish) while we update the table
    preserveTopScroll(callback = () => {}) {
        this._preservingScroll = true;
        const scrollContainer = this.getScrollContainer();
        // Use current reading rather than trusting a cached value
        const prevTop = scrollContainer === window ? window.scrollY : scrollContainer.scrollTop;

        // Temporarily disable scroll anchoring so the browser doesn't "help"
        const el = scrollContainer === window ? document.documentElement : scrollContainer;
        el.classList.add('no-overflow-anchor');

        // Do the DOM work (likely refreshTable)
        callback();

        // Restore on next frame after layout
        requestAnimationFrame(() => {
            if (scrollContainer === window) {
                window.scrollTo(0, prevTop);
            } else {
                scrollContainer.scrollTop = prevTop;
            }
            el.classList.remove('no-overflow-anchor');
            this._preservingScroll = false;
        });
    },
});
